import { useRef, useState } from "react";
import moment from "moment";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AviTracerApi, IfrPlanningOverrides, IfrRouteFindInput, IfrRouteFound, IfrRouteResult } from "../../../avitracerApi";
import { Aircraft } from "../../../models/aircraft";
import { Route } from "../../../models/route";
import PlanIfrRouteModal, { IfrRouteSelectResult } from "../../../components/PlanIfrRouteModal";
import ListAicraftPage from "../aircraft/ListAircraftPage";
import { useAppDispatch } from "../../../store/hooks";
import { createRoute } from "../../../store/routesSlice";

type ChosenIfrRoute = {
  field15: string;
  level?: number;
  departure?: string;
  destination?: string;
  alternate1?: string;
  alternate2?: string;
  distanceNM?: number;
};

// New IFR company route: a Field-15 template planned & checked in the IFR Route Planner against a
// chosen aircraft (the validation template — asked for every time, never remembered), then
// re-validated with IFPUV at save time ("validate & save"). The snapshot stored with the route is
// advisory only; routes are re-checked whenever they are used on a flight.
function AddIfrRoutePage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  //?rules=Y|Z from the chooser's Mixed card — otherwise a pure IFR (I) route template.
  const rulesParam = (searchParams.get("rules") ?? "").toUpperCase();
  const flightRules: "I" | "Y" | "Z" = rulesParam === "Y" || rulesParam === "Z" ? rulesParam : "I";
  const isMixed = flightRules === "Y" || flightRules === "Z";

  const [name, setName] = useState("");
  const [aircraft, setAircraft] = useState<Aircraft | undefined>(undefined);
  const [pickingAircraft, setPickingAircraft] = useState(false);
  //Open the planner FIRST — the user plans the route, then returns to name it and pick an aircraft.
  const [showPlanner, setShowPlanner] = useState(true);
  const [chosen, setChosen] = useState<ChosenIfrRoute | undefined>(undefined);
  //The armed option exactly as the planner resolved it — waypoint coordinates + the IFPUV result it
  //was already checked with. Kept so saving does not have to ask autorouter the same question twice.
  const [armedResult, setArmedResult] = useState<IfrRouteFound | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  //shown when Check is pressed with no aircraft — explains the one-off validation + offers the picker
  const [showValidateAircraftPrompt, setShowValidateAircraftPrompt] = useState(false);
  //A Check pressed without an aircraft does not fail — it PAUSES here until the aircraft question is
  //answered, then carries on by itself. This holds the waiting check's resolver; settling it with an
  //aircraft resumes the check, settling it with undefined (cancel) abandons it.
  const pendingAircraftResolve = useRef<((ac?: Aircraft) => void) | undefined>(undefined);

  const settleAircraftRequest = (ac?: Aircraft) => {
    const resolve = pendingAircraftResolve.current;
    pendingAircraftResolve.current = undefined;
    resolve?.(ac);
  };

  //Dedupes concurrent asks from the parallel route-find runs (see findIfrRoutes).
  const pendingAircraftAsk = useRef<Promise<Aircraft | undefined> | undefined>(undefined);

  const requestValidationAircraft = (): Promise<Aircraft | undefined> => {
    setShowValidateAircraftPrompt(true);
    return new Promise<Aircraft | undefined>((resolve) => {
      pendingAircraftResolve.current = resolve;
    });
  };
  //editable route distance (autorouter's value, correctable if wrong) — drives EET when the route is used
  const [distanceText, setDistanceText] = useState("");

  const close = () => navigate("/routes");

  //Closing the planner (X): during initial planning (no route armed yet) there is nothing to return
  //to, so confirm discard and leave entirely. If a route was already armed, X just returns to the
  //name/aircraft form so it can be re-planned without losing the work.
  const handlePlannerHide = () => {
    if (chosen) {
      setShowPlanner(false);
      return;
    }
    setShowDiscardConfirm(true);
  };

  //Aircraft saved as IFR-certified before the MTOW field existed can lack it — catch it here with
  //a pointer to the fix instead of letting route-find fail with a raw backend error.
  const aircraftMtow = aircraft ? Number(aircraft.wb?.maxMass ?? aircraft.mtow) : undefined;
  const aircraftMissingMtow = aircraft !== undefined && !(aircraftMtow! > 0);

  //True when the armed route already carries a clean IFPUV result and usable geometry from the planner
  //— saving then needs no second autorouter call, and no aircraft question.
  const routeAlreadyValidated =
    aircraft !== undefined &&
    chosen !== undefined &&
    armedResult !== undefined &&
    armedResult.field15 === chosen.field15 &&
    (armedResult.validation ?? "").trim().toUpperCase().startsWith("NO ERROR") &&
    (armedResult.waypoints ?? []).filter((w) => w.lat !== undefined && w.lon !== undefined).length >= 2;

  const icao = (v?: string | Date): string | undefined => {
    const s = (v ?? "").toString().toUpperCase().trim();
    return /^[A-Z]{4}$/.test(s) ? s : undefined;
  };

  //forAircraft lets a caller pass an aircraft that was only just chosen — the `aircraft` state has not
  //re-rendered yet at that point, so reading it here would still see the old (empty) value.
  const buildFindInput = (extra?: Partial<IfrRouteFindInput>, forAircraft?: Aircraft): IfrRouteFindInput => {
    const ac = forAircraft ?? aircraft!;
    return {
      departure: "",
      destination: "",
      //templates have no real off-block time — validate against tomorrow, same as a typical filing
      departureDateTime: moment().add(1, "day").toDate(),
      level: 0,
      aircraft: {
        registration: ac.registration,
        icaoType: ac.icaoType,
        equipmentCodes: ac.equipmentCodes ?? "S",
        surveillanceEquipmentCodes: ac.surveillanceEquipmentCodes ?? "N",
        cruiseSpeedKt: Number(ac.cruiseSpeed?.value),
        fuelUnit: ac.fuelUnit,
        fuelPerHour: Number(ac.fuelPerHour),
        maxMass: Number(ac.wb?.maxMass ?? ac.mtow),
      },
      ...extra,
    };
  };

  //Route-find needs the aircraft's performance, but asking for it UP FRONT means a permanently greyed
  //"Find routes" with no stated reason, on a screen that covers the aircraft field. So do exactly what
  //Check does: run, and if no aircraft is set yet, PAUSE for the picker and carry on with the answer —
  //the user never presses Find twice. Cancelling throws Error(""), which the planner treats as a silent
  //abort. Both planner actions now behave identically.
  const findIfrRoutes = async (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<IfrRouteResult[]> => {
    //The planner fans out one call per optimize profile, so several land here at once. Share a single
    //in-flight request or each would raise its own prompt and the user would answer the same question
    //three times.
    let ac = aircraft;
    if (!ac) {
      pendingAircraftAsk.current = pendingAircraftAsk.current ?? requestValidationAircraft()
        .finally(() => { pendingAircraftAsk.current = undefined });
      ac = await pendingAircraftAsk.current;
      if (!ac) throw new Error("");
    }
    //An IFR-certified aircraft saved before the MTOW field existed cannot be routed — say so plainly
    //rather than letting autorouter fail with a raw backend error.
    if (!(Number(ac.wb?.maxMass ?? ac.mtow) > 0)) {
      throw new Error(`${ac.name} has no MTOW — set it in Aircraft → IFR / PBN (or assign a W&B profile) to find routes.`);
    }
    const resp = await AviTracerApi.findIfrRoutesFromData(buildFindInput({ optimizes, levels, ...overrides }, ac));
    return resp.results;
  };

  //Building works without an aircraft; in-planner Check needs one for the EUROCONTROL FPL. If none is
  //selected yet, ask for one and WAIT — the check then continues on its own with the chosen aircraft,
  //so the user never has to press Check a second time. Cancelling throws Error(""), which suppresses
  //the planner's error line and skips pushing a failed result.
  const checkManualRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<IfrRouteResult> => {
    let ac = aircraft;
    if (!ac) {
      ac = await requestValidationAircraft();
      if (!ac) throw new Error("");
    }
    const resp = await AviTracerApi.checkIfrRouteFromData({ ...buildFindInput({ ...overrides }, ac), field15, flightRules });
    return resp.result;
  };

  //"Use this route" in the planner just captures the template here — nothing is persisted yet.
  //On the first arm, prefill the route name from DEP-DEST so the user only has to pick an aircraft.
  const selectIfrRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<IfrRouteSelectResult> => {
    if (!field15) {
      setChosen(undefined);
      setArmedResult(undefined);
      return { ifpsRoute: undefined };
    }
    const dep = icao(overrides?.departure);
    const dest = icao(overrides?.destination);
    const dist = Number(overrides?.distanceNM);
    setChosen({
      field15,
      level: overrides?.level,
      departure: dep,
      destination: dest,
      alternate1: icao(overrides?.alternate1),
      alternate2: icao(overrides?.alternate2),
      distanceNM: Number.isFinite(dist) && dist > 0 ? Math.round(dist) : undefined,
    });
    if (name.trim().length === 0 && dep && dest) setName(`${dep}-${dest}`);
    setDistanceText(Number.isFinite(dist) && dist > 0 ? String(Math.round(dist)) : "");
    setShowPlanner(false);
    return { ifpsRoute: field15 };
  };

  const handleValidateAndSave = async () => {
    if (!aircraft || !chosen || name.trim().length === 0) return;
    setSaving(true);
    setError(undefined);
    try {
      if (!chosen.departure || !chosen.destination) throw new Error("The route needs ICAO departure and destination aerodromes.");
      //Z (VFR first) carries its IFR level inside the route string, so no base level is required.
      if (flightRules !== "Z" && (!chosen.level || chosen.level <= 0)) {
        throw new Error("The route needs a flight level — set it in the planner before arming the route.");
      }

      //Only an IFPS-clean route can be armed for a route template (the planner disables "Use this
      //route" otherwise), so what is armed here is already validated and carries its own geometry —
      //no second autorouter round-trip. The fallback below is a safety net for the one thing arming
      //does not guarantee: an option that came back without usable waypoint coordinates.
      let armed: IfrRouteFound | undefined =
        armedResult && armedResult.field15 === chosen.field15 ? armedResult : undefined;
      if (!armed || (armed.waypoints ?? []).filter((w) => w.lat !== undefined && w.lon !== undefined).length < 2) {
        const resp = await AviTracerApi.checkIfrRouteFromData({
          ...buildFindInput({
            departure: chosen.departure,
            destination: chosen.destination,
            level: chosen.level,
            alternate1: chosen.alternate1,
            alternate2: chosen.alternate2,
          }),
          field15: chosen.field15,
          flightRules,
        });
        const result = resp.result;
        if (result.status !== "found") {
          throw new Error(result.status === "failed" ? result.error : "Validation did not complete — try again.");
        }
        armed = result;
      }

      if (!(armed.validation ?? "").trim().toUpperCase().startsWith("NO ERROR")) {
        throw new Error("IFPS validation failed:\n" + armed.validation);
      }

      const coords = (armed.waypoints ?? []).filter((w) => w.lat !== undefined && w.lon !== undefined);
      if (coords.length < 2) throw new Error("Could not resolve the route's coordinates.");
      const first = coords[0];
      const last = coords[coords.length - 1];

      const route: Route = {
        name: name.trim(),
        departure: { name: chosen.departure, lat: first.lat!, lon: first.lon! },
        destination: { name: chosen.destination, lat: last.lat!, lon: last.lon! },
        waypoints: coords.slice(1, -1).map((w) => ({ name: w.ident ?? w.name, lat: w.lat!, lon: w.lon! })),
        intermediateStops: [],
        //user-corrected distance wins over autorouter's; falls back to the freshly computed value
        distanceNM: Number(distanceText) > 0 ? Math.round(Number(distanceText)) : Math.round(armed.routeDist ?? 0),
        //Pure IFR (I) is addressed to the IFPS units at filing time, so it needs none. Mixed rules (Y/Z)
        //DO need the VFR segments' ATS addresses — the backend resolves and stores those on create.
        aftnAddresses: [],
        altn1: chosen.alternate1,
        altn2: chosen.alternate2,
        isIfr: true, //IFR-family (covers mixed) so it is offered in the IFR planner's saved-route list
        flightRules,
        ifpsRoute: armed.field15,
        defaultLevel: chosen.level,
        ifrValidation: {
          at: new Date().toISOString(),
          aircraftRegistration: aircraft.registration,
          level: chosen.level ?? 0,
        },
      };

      await dispatch(createRoute(route)).unwrap();
      navigate("/routes");
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal show onHide={close} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isMixed ? "New Mixed VFR/IFR Route" : "New IFR Route"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {
            <>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Route name</Form.Label>
                <Form.Control value={name} placeholder="e.g. LGMG-LGKM via M749" onChange={(e) => setName(e.target.value)} />
              </Form.Group>

              {chosen && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Distance (NM)</Form.Label>
                  <Form.Control
                    value={distanceText}
                    inputMode="numeric"
                    placeholder="—"
                    style={{ maxWidth: "160px" }}
                    onChange={(e) => setDistanceText(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  <Form.Text muted>From EUROCONTROL routing — adjust if it looks wrong. Used to estimate EET (with the flying aircraft's speed) whenever the route is used.</Form.Text>
                </Form.Group>
              )}

              {/* Only shown while an aircraft is still needed. Once the planner has armed an
                  IFPS-clean route the check is already done, so the section disappears entirely. */}
              {!routeAlreadyValidated && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Aircraft for validation</Form.Label>
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Control readOnly value={aircraft ? `${aircraft.name} (${aircraft.registration})` : ""} placeholder="No aircraft selected" />
                    <Button variant="dark" onClick={() => setPickingAircraft(true)}>
                      {aircraft ? "Change" : "Select"}
                    </Button>
                  </div>
                  <Form.Text muted>
                    The route is checked with this aircraft's equipment and performance. It is not stored with the route — every
                    validation asks again.
                  </Form.Text>
                  {aircraftMissingMtow && (
                    <Alert variant="warning" className="py-2 mt-2 mb-0">
                      <small>
                        {aircraft!.name} has no MTOW — IFR route-finding needs it. Set "MTOW in kg" in Aircraft → IFR / PBN (or
                        assign a W&amp;B profile), then re-select the aircraft.
                      </small>
                    </Alert>
                  )}
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">Route (Field 15)</Form.Label>
                {chosen ? (
                  <div className="border rounded p-2 bg-light">
                    <div className="font-monospace small">{chosen.field15}</div>
                    <div className="small text-muted mt-1">
                      {chosen.departure ?? "?"} → {chosen.destination ?? "?"}
                      {chosen.level ? ` · FL${String(chosen.level).padStart(3, "0")}` : ""}
                      {chosen.alternate1 ? ` · ALTN ${chosen.alternate1}` : ""}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small">No route yet — plan one on the map with EUROCONTROL validation.</div>
                )}
                <div className="d-grid mt-2">
                  <Button variant="outline-dark" onClick={() => setShowPlanner(true)}>
                    {chosen ? "Re-plan route" : "Plan route"}
                  </Button>
                </div>
                <Form.Text muted>
                  {routeAlreadyValidated
                    ? "Built and validated on the map — saving stores it as planned."
                    : "Build the route on the map. Pick an aircraft to validate in the planner, or validate at save."}
                </Form.Text>
              </Form.Group>

              {error && (
                <Alert variant="danger" className="py-2" style={{ whiteSpace: "pre-wrap" }}>
                  <small>{error}</small>
                </Alert>
              )}

              <div className="d-grid">
                {/* Nothing is re-validated when the planner already armed a clean route — this just
                    stores it, so it says Save. Only the fallback path (armed option came back without
                    coordinates) actually calls EUROCONTROL again. */}
                <Button variant="dark" disabled={saving || !aircraft || aircraftMissingMtow || !chosen || name.trim().length === 0} onClick={handleValidateAndSave}>
                  {routeAlreadyValidated
                    ? saving
                      ? "Saving…"
                      : "Save"
                    : saving
                    ? "Validating…"
                    : "Validate & Save"}
                </Button>
              </div>
            </>
          }
        </Modal.Body>
      </Modal>

      {/* Aircraft picker as a standalone stacking modal so it appears over the planner too, when the
          user triggers it from an in-planner Check. */}
      <Modal
        show={pickingAircraft}
        onHide={() => {
          setPickingAircraft(false);
          //dismissed without choosing — let any check that was waiting on this give up
          settleAircraftRequest(undefined);
        }}
        fullscreen
        className="ifr-stacked-modal"
        backdropClassName="ifr-stacked-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Aircraft to validate with</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ListAicraftPage
            ifrPick
            onAircraftSelected={(a) => {
              setAircraft(a);
              setPickingAircraft(false);
              //resumes the Check that asked for this aircraft, so it runs without a second press
              settleAircraftRequest(a);
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Pressed Check with no aircraft: explain the one-off validation, then offer the picker. */}
      <Modal
        show={showValidateAircraftPrompt}
        onHide={() => {
          setShowValidateAircraftPrompt(false);
          settleAircraftRequest(undefined);
        }}
        centered
        className="ifr-stacked-modal"
        backdropClassName="ifr-stacked-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose an aircraft</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Finding and checking a route both build a complete flight plan for EUROCONTROL, so they need an
          aircraft's equipment and performance.
          <div className="mt-2">
            This is a <b>one-off check for now</b> — the aircraft is not stored with the route. When you use this route on a
            flight or flight plan, it is <b>re-validated with that flight's aircraft before filing</b>.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowValidateAircraftPrompt(false);
              settleAircraftRequest(undefined);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="dark"
            onClick={() => {
              setShowValidateAircraftPrompt(false);
              setPickingAircraft(true);
            }}
          >
            Choose aircraft
          </Button>
        </Modal.Footer>
      </Modal>

      {showPlanner && (
        <PlanIfrRouteModal
          show={showPlanner}
          onHide={handlePlannerHide}
          isIfr={true}
          flightRules={flightRules}
          cruiseSpeedKt={aircraft ? Number(aircraft.cruiseSpeed?.value) : undefined}
          ifpsRoute={chosen?.field15}
          baseLevel={chosen?.level}
          departure={chosen?.departure}
          destination={chosen?.destination}
          alternate1={chosen?.alternate1}
          alternate2={chosen?.alternate2}
          departureDateTime={moment().add(1, "day").toDate()}
          //Always supplied: findIfrRoutes asks for the aircraft itself when one is needed, so the
          //button is never greyed for a reason the planner cannot show.
          findIfrRoutesAction={findIfrRoutes}
          checkManualRouteAction={checkManualRoute}
          selectIfrRouteAction={selectIfrRoute}
          onRouteSelected={() => {}}
          onRouteArmed={setArmedResult}
          persistedOnSelect={false}
          entityKind="route"
        />
      )}

      <Modal show={showDiscardConfirm} onHide={() => setShowDiscardConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Discard route?</Modal.Title>
        </Modal.Header>
        <Modal.Body>You haven't saved this route yet. Discard it and close the planner?</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDiscardConfirm(false)}>
            Keep planning
          </Button>
          <Button variant="danger" onClick={() => navigate("/routes")}>
            Discard
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AddIfrRoutePage;
